import React, { useState, useEffect } from 'react';
import {
  fetchSectionsForMedia,
  fetchSectionMedia,
  createSectionMedia,
  updateSectionMedia,
  deleteSectionMedia,
  generateSectionVideos,
  generateMultiLanguageVideos,
  uploadImage,
  translateText
} from '../API/API';
import { fetchCourseOrderData, CourseMetadataItem } from '../API/Course/CourseAPI';
import { AVAILABLE_LANGUAGES } from '../shared/LanguageControlsGrid/LanguageControlsGrid';
import BackButton from '../shared/BackButton/BackButton';
import ImageCropModal from './ImageCropModal';
import styles from './SectionMediaManager.module.css';

// Language mapping for API calls
const LANG_CODE_MAP: Record<string, string> = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

interface SectionMediaItem {
  languagePair: string;
  sectionKey: string;
  sectionId: string;
  baseLanguage: string;
  targetLanguage: string;
  contentType?: 'notes' | 'video'; // Distinguishes notes from videos
  noteImageUrl: string;
  noteImageDescription: string;
  noteText: string;
  videoUrl: string;
  videoScript: string;
  videoThumbnailUrl: string;
  videoGenerated: string;
  videoDuration: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  additionalImages: string[];
  viewCount: number;
}

interface GroupedSectionMedia {
  sectionId: string;
  sectionNumber: string;
  items: SectionMediaItem[];
}


interface AlertState {
  type: 'success' | 'error' | 'info';
  message: string;
}

const SectionMediaManager: React.FC = () => {
  // Language selection state
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Component-specific state
  const [mediaItems, setMediaItems] = useState<SectionMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SectionMediaItem | null>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);

  // Course sections state
  const [courseSections, setCourseSections] = useState<CourseMetadataItem[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);

  // Base language filter (for viewing different language versions)
  const [selectedBaseLanguage, setSelectedBaseLanguage] = useState<string>('EN');

  // Video-specific state
  const [narrationLanguage, setNarrationLanguage] = useState<string>('EN'); // The language spoken in the video

  // Computed values
  const sourceLanguageCode = LANG_CODE_MAP[sourceLanguage];
  const targetLanguageCode = sourceLanguageCode || 'EN'; // Actually the target language for sections

  // Form fields
  const [formData, setFormData] = useState({
    sectionId: '',
    noteImageUrl: '',
    noteImageDescription: '',
    noteText: '',
    videoUrl: '',
    videoScript: '',
    videoThumbnailUrl: '',
    videoGenerated: 'false',
    videoDuration: 0,
    status: 'draft',
    additionalImages: [] as string[],
    audioUrl: ''
  });

  // Video generation workflow state
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [generatedVideos, setGeneratedVideos] = useState<any[]>([]);
  const [testingMode, setTestingMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [savingToDB, setSavingToDB] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // Image crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const [fileNameToCrop, setFileNameToCrop] = useState<string>('');

  // Content type toggle state (notes vs videos)
  const [contentType, setContentType] = useState<'notes' | 'video'>('notes');

  // Load course sections when source language changes
  useEffect(() => {
    if (!sourceLanguage) {
      setCourseSections([]);
      return;
    }

    const loadSections = async () => {
      setLoadingSections(true);
      try {
        const langCode = sourceLanguageCode;
        if (!langCode) {
          console.error('Invalid language selected');
          setLoadingSections(false);
          return;
        }

        const sectionsData = await fetchCourseOrderData(langCode);
        setCourseSections(sectionsData);
      } catch (error) {
        console.error('Error loading sections:', error);
        setAlert({ type: 'error', message: 'Failed to load sections' });
      } finally {
        setLoadingSections(false);
      }
    };

    loadSections();
  }, [sourceLanguage, sourceLanguageCode]);
  
  // Load media items when contentType or selectedBaseLanguage changes (always show all sections)
  useEffect(() => {
    if (sourceLanguage) {
      loadMediaItems();
    }
  }, [sourceLanguage, contentType, selectedBaseLanguage]);

  // Clear generated videos when content type or base language changes
  useEffect(() => {
    setGeneratedVideos([]);
  }, [contentType, selectedBaseLanguage]);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);


  // Helper function to get section number from course sections
  const getSectionNumber = (sectionId: string): string => {
    const section = courseSections.find(
      cs => cs.Vocabulary_List === sectionId || cs.Identifier === sectionId
    );
    return section?.Identifier || '?';
  };

  // Helper function to group media items by section
  const groupMediaBySection = (items: SectionMediaItem[]): GroupedSectionMedia[] => {
    const grouped = items.reduce((acc, item) => {
      if (!acc[item.sectionId]) {
        acc[item.sectionId] = {
          sectionId: item.sectionId,
          sectionNumber: getSectionNumber(item.sectionId),
          items: []
        };
      }
      acc[item.sectionId].items.push(item);
      return acc;
    }, {} as Record<string, GroupedSectionMedia>);

    // Convert to array and sort by section number
    return Object.values(grouped).sort((a, b) => {
      const numA = parseInt(a.sectionNumber) || 999;
      const numB = parseInt(b.sectionNumber) || 999;
      return numA - numB;
    });
  };

  const loadMediaItems = async () => {
    setLoading(true);
    try {
      // Fetch media for the selected target language and content type
      // baseLanguage = selected base language filter (e.g., 'EN', 'ES')
      // targetLanguage = the language we're teaching (from main dropdown)
      // contentType = 'notes' or 'video' based on toggle
      const data = await fetchSectionMedia(selectedBaseLanguage, targetLanguageCode, contentType);
      console.log('Raw API response:', data);

      // Handle different response formats from the API
      let parsedData;
      if (typeof data === 'string') {
        // If the entire response is a string, parse it
        parsedData = JSON.parse(data);
      } else if (data.body && typeof data.body === 'string') {
        // If there's a body property that's a string, parse it
        parsedData = JSON.parse(data.body);
      } else {
        // Already parsed or direct object
        parsedData = data;
      }

      console.log('Parsed data:', parsedData);
      const items = parsedData.items || [];

      // Show all items for the selected target language and base language
      console.log(`Loaded ${items.length} items for ${selectedBaseLanguage} -> ${targetLanguageCode}`);
      setMediaItems(items);

      // Clear any previous error messages on successful load
      setAlert(null);
    } catch (error) {
      console.error('Error loading media items:', error);
      setAlert({ type: 'error', message: 'Failed to load media items' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item?: SectionMediaItem) => {
    if (item) {
      // Editing existing item
      setEditingItem(item);
      setFormData({
        sectionId: item.sectionId,
        noteImageUrl: item.noteImageUrl || '',
        noteImageDescription: item.noteImageDescription || '',
        noteText: item.noteText || '',
        videoUrl: item.videoUrl || '',
        videoScript: item.videoScript || '',
        videoThumbnailUrl: item.videoThumbnailUrl || '',
        videoGenerated: item.videoGenerated || 'false',
        videoDuration: item.videoDuration || 0,
        status: item.status || 'draft',
        additionalImages: Array.isArray(item.additionalImages) ? item.additionalImages : [],
        audioUrl: ''
      });
    } else {
      // Creating new item
      setEditingItem(null);
      setFormData({
        sectionId: courseSections.length > 0 ? courseSections[0].Vocabulary_List : '',
        noteImageUrl: '',
        noteImageDescription: '',
        noteText: '',
        videoUrl: '',
        videoScript: '',
        videoThumbnailUrl: '',
        videoGenerated: 'false',
        videoDuration: 0,
        status: 'draft',
        additionalImages: [],
        audioUrl: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!formData.sectionId) {
      setAlert({ type: 'error', message: 'Please select a section' });
      return;
    }

    // Validation based on content type
    if (contentType === 'notes') {
      if (!formData.noteImageUrl && !formData.noteText) {
        setAlert({ type: 'error', message: 'Please provide at least an image or note text' });
        return;
      }
    } else if (contentType === 'video') {
      if (!formData.videoUrl && !formData.noteImageUrl) {
        setAlert({ type: 'error', message: 'Please provide a video URL or background image' });
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        sectionId: formData.sectionId,
        baseLanguage: selectedBaseLanguage,
        targetLanguage: targetLanguageCode,
        contentType: contentType, // Include content type
        createdBy: 'admin'
      };

      if (editingItem) {
        // Update existing item
        await updateSectionMedia(editingItem.sectionId, payload);
        setAlert({ type: 'success', message: `${contentType === 'video' ? 'Video' : 'Notes'} updated successfully` });
      } else {
        // Create new item
        await createSectionMedia(payload);
        setAlert({ type: 'success', message: `${contentType === 'video' ? 'Video' : 'Notes'} created successfully` });
      }

      setDialogOpen(false);
      loadMediaItems();
    } catch (error) {
      console.error('Error saving media:', error);
      setAlert({ type: 'error', message: 'Failed to save media' });
    }
  };

  const handleDelete = async (item: SectionMediaItem) => {
    if (!window.confirm(`Are you sure you want to delete ${item.contentType || 'this media'} for ${item.sectionId}?`)) {
      return;
    }

    try {
      await deleteSectionMedia(item.sectionId, item.baseLanguage, item.targetLanguage, item.contentType);

      setAlert({ type: 'success', message: 'Media deleted successfully' });
      loadMediaItems();
    } catch (error) {
      console.error('Error deleting media:', error);
      setAlert({ type: 'error', message: 'Failed to delete media' });
    }
  };

  // Language change handler
  const handleSourceLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceLanguage(e.target.value);
    setMediaItems([]); // Clear media items
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageFileSelect = async (file: File) => {
    if (!file) return;

    // Upload directly without cropping
    setUploadingImage(true);
    setSelectedImageFile(file);

    try {
      const imageUrl = await uploadImage(file.name, file);
      handleInputChange('noteImageUrl', imageUrl);
      setAlert({ type: 'success', message: 'Image uploaded successfully' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setAlert({ type: 'error', message: 'Failed to upload image' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenCropModal = () => {
    if (!selectedImageFile) return;

    // Create a temporary URL for the image to display in crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setFileNameToCrop(selectedImageFile.name);
      setShowCropModal(true);
    };
    reader.readAsDataURL(selectedImageFile);
  };

  const handleCropComplete = async (croppedImageFile: File) => {
    setShowCropModal(false);
    setUploadingImage(true);

    try {
      const imageUrl = await uploadImage(croppedImageFile.name, croppedImageFile);
      handleInputChange('noteImageUrl', imageUrl);
      setAlert({ type: 'success', message: 'Image uploaded successfully' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setAlert({ type: 'error', message: 'Failed to upload image' });
    } finally {
      setUploadingImage(false);
      setImageToCrop('');
      setFileNameToCrop('');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop('');
    setFileNameToCrop('');
  };

  const handleGenerateTestAudio = async () => {
    if (!formData.noteImageUrl) {
      setAlert({ type: 'error', message: 'Please upload an image first' });
      return;
    }

    if (!formData.videoScript) {
      setAlert({ type: 'error', message: 'Please enter script text' });
      return;
    }

    if (!formData.sectionId) {
      setAlert({ type: 'error', message: 'Section ID is required' });
      return;
    }

    // Close modal and show loading screen
    setDialogOpen(false);
    setGeneratingVideos(true);
    setGenerationProgress('Generating English audio for testing...');

    try {
      setGenerationProgress('Synthesizing English speech...');
      
      // Only generate for English (using the multi-language function but limiting to English)
      const audioResults = await generateMultiLanguageVideos(
        formData.noteImageUrl,
        formData.videoScript,
        ['EN']
      );

      if (audioResults.success) {
        // Process results for preview
        const mediaResults = [];
        for (const audioResult of audioResults.results) {
          const placeholderVideoUrl = `${formData.noteImageUrl}?audio=${encodeURIComponent(audioResult.audioUrl)}&lang=${audioResult.language}`;
          
          mediaResults.push({
            language: audioResult.language,
            translatedText: audioResult.translatedText,
            audioUrl: audioResult.audioUrl,
            videoUrl: placeholderVideoUrl,
            imageUrl: formData.noteImageUrl,
            s3Key: audioResult.audioS3Key,
            visemes: audioResult.visemes
          });
        }

        // Show preview instead of saving
        setPreviewData(mediaResults);
        setPreviewMode(true);
        
        setGenerationProgress('');
        setAlert({ 
          type: 'success', 
          message: `Test audio generated successfully! Review below and click "Confirm & Save" to save to database.` 
        });
      }
    } catch (error) {
      console.error('Error generating test audio:', error);
      setAlert({ type: 'error', message: 'Failed to generate test audio' });
      setGenerationProgress('');
    } finally {
      setGeneratingVideos(false);
    }
  };

  const handleGenerateVideos = async () => {
    if (!formData.noteImageUrl || !formData.videoScript) {
      setAlert({ type: 'error', message: 'Please upload an image and enter video script text' });
      return;
    }

    if (!formData.sectionId) {
      setAlert({ type: 'error', message: 'Section ID is required' });
      return;
    }

    // Close modal and show loading screen
    setDialogOpen(false);
    setGeneratingVideos(true);
    setGenerationProgress('Starting video generation...');

    try {
      setGenerationProgress('Translating text to multiple languages...');
      
      // Generate audio for all languages except the source language
      const allLanguages = ['EN', 'ES', 'FR', 'DE', 'IT', 'PT'];
      const languagesToGenerate = allLanguages.filter(lang => lang !== selectedBaseLanguage);
      
      const audioResults = await generateMultiLanguageVideos(
        formData.noteImageUrl,
        formData.videoScript,
        languagesToGenerate
      );

      if (audioResults.success) {
        // Process results for preview
        const mediaResults = [];
        for (const audioResult of audioResults.results) {
          const placeholderVideoUrl = `${formData.noteImageUrl}?audio=${encodeURIComponent(audioResult.audioUrl)}&lang=${audioResult.language}`;
          
          mediaResults.push({
            language: audioResult.language,
            translatedText: audioResult.translatedText,
            audioUrl: audioResult.audioUrl,
            videoUrl: placeholderVideoUrl,
            imageUrl: formData.noteImageUrl,
            s3Key: audioResult.audioS3Key,
            visemes: audioResult.visemes
          });
        }

        // Show preview instead of saving
        setPreviewData(mediaResults);
        setPreviewMode(true);
        
        setGenerationProgress('');
        setAlert({ 
          type: 'success', 
          message: `Audio files generated successfully for ${mediaResults.length} languages! Review below and click "Confirm & Save" to save to database.` 
        });
      }
    } catch (error) {
      console.error('Error generating videos:', error);
      setAlert({ type: 'error', message: 'Failed to generate audio files' });
      setGenerationProgress('');
    } finally {
      setGeneratingVideos(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!previewData.length) return;

    setSavingToDB(true);
    setGenerationProgress('Saving to database...');

    try {
      const sectionMediaEntries = [];

      for (const media of previewData) {
        // Determine if this is a video or note based on the presence of audioUrl
        const isVideo = !!media.audioUrl;

        const sectionMediaPayload: any = {
          sectionId: formData.sectionId,
          baseLanguage: media.language, // The language of the content (EN, ES, FR, etc.)
          targetLanguage: targetLanguageCode, // The target language we're teaching (selected at start)
          contentType: isVideo ? 'video' : 'notes',
          noteImageUrl: media.imageUrl,
          noteText: media.translatedText,
          noteImageDescription: media.noteImageDescription || '',
          status: 'published'
        };

        // Add video-specific fields if this is a video
        if (isVideo) {
          sectionMediaPayload.audioUrl = media.audioUrl;
          sectionMediaPayload.videoUrl = media.videoUrl;
          sectionMediaPayload.s3Key = media.s3Key;
          sectionMediaPayload.visemes = media.visemes;
          sectionMediaPayload.videoGenerated = 'true';
        }

        console.log(`Saving section media for ${media.language}...`);
        const sectionMediaResult = await createSectionMedia(sectionMediaPayload);
        sectionMediaEntries.push(sectionMediaResult);
      }

      // Move preview data to saved data
      setGeneratedVideos(previewData);
      setPreviewData([]);
      setPreviewMode(false);

      setAlert({
        type: 'success',
        message: `Successfully saved ${previewData.length} media entries to database!`
      });

      // Reload the media items to show the newly saved entries
      loadMediaItems();

    } catch (error) {
      console.error('Error saving to database:', error);
      setAlert({ type: 'error', message: 'Failed to save to database' });
    } finally {
      setSavingToDB(false);
      setGenerationProgress('');
    }
  };

  const handleDiscardPreview = () => {
    setPreviewData([]);
    setPreviewMode(false);
    setAlert({ type: 'info', message: 'Preview discarded. Generated audio files remain in temporary storage.' });
  };

  const handleGenerateMultiLanguageNotes = async () => {
    if (!formData.noteImageUrl || !formData.noteText) {
      setAlert({ type: 'error', message: 'Please upload an image and enter note text' });
      return;
    }

    if (!formData.sectionId) {
      setAlert({ type: 'error', message: 'Section ID is required' });
      return;
    }

    // Close modal and show loading screen
    setDialogOpen(false);
    setGeneratingVideos(true);
    setGenerationProgress('Translating notes to multiple languages...');

    try {
      const allLanguages = ['EN', 'ES', 'FR', 'DE', 'IT', 'PT'];

      // Determine the source language based on narrationLanguage or default to EN
      const sourceLanguage = narrationLanguage || 'EN';

      // Translate to all other languages
      const languagesToGenerate = allLanguages.filter(lang => lang !== sourceLanguage);

      const translationResults = [];

      for (const targetLang of languagesToGenerate) {
        setGenerationProgress(`Translating to ${targetLang}...`);

        const translatedText = await translateText(formData.noteText, sourceLanguage, targetLang);

        translationResults.push({
          language: targetLang,
          translatedText: translatedText,
          imageUrl: formData.noteImageUrl,
          noteImageDescription: formData.noteImageDescription || ''
        });
      }

      // Also include the original language
      translationResults.push({
        language: sourceLanguage,
        translatedText: formData.noteText,
        imageUrl: formData.noteImageUrl,
        noteImageDescription: formData.noteImageDescription || ''
      });

      // Show preview
      setPreviewData(translationResults);
      setPreviewMode(true);

      setGenerationProgress('');
      setAlert({
        type: 'success',
        message: `Notes translated successfully for ${translationResults.length} languages! Review below and click "Confirm & Save" to save to database.`
      });
    } catch (error) {
      console.error('Error translating notes:', error);
      setAlert({ type: 'error', message: 'Failed to translate notes' });
      setGenerationProgress('');
    } finally {
      setGeneratingVideos(false);
    }
  };

  return (
    <div className={styles.container}>
      <BackButton />
      
      <div className={styles.header}>
        <h1 className={styles.title}>Section Media Manager</h1>

        <button
          className={styles.addButton}
          onClick={() => handleOpenDialog()}
          disabled={loading || !sourceLanguage || courseSections.length === 0}
          title={!sourceLanguage ? "Select a language first" : courseSections.length === 0 ? "No sections available" : "Add new media"}
        >
          + Add Media
        </button>
      </div>
      
      {/* Language and Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label htmlFor="targetLanguage">Target Language (Learning):</label>
          <select
            id="targetLanguage"
            value={sourceLanguage}
            onChange={handleSourceLanguageChange}
            disabled={loading}
          >
            <option value="">-- Select Language --</option>
            {AVAILABLE_LANGUAGES.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="baseLanguage">Base Language (Content):</label>
          <select
            id="baseLanguage"
            value={selectedBaseLanguage}
            onChange={(e) => setSelectedBaseLanguage(e.target.value)}
            disabled={loading || !sourceLanguage}
          >
            <option value="EN">English</option>
            <option value="ES">Spanish</option>
            <option value="FR">French</option>
            <option value="DE">German</option>
            <option value="IT">Italian</option>
            <option value="PT">Portuguese</option>
          </select>
        </div>
      </div>

      {/* Content Type Toggle */}
      <div className={styles.contentTypeToggle}>
        <label className={styles.toggleLabel}>Content Type:</label>
        <div className={styles.toggleButtons}>
          <button
            className={`${styles.toggleButton} ${contentType === 'notes' ? styles.toggleButtonActive : ''}`}
            onClick={() => setContentType('notes')}
            disabled={loading}
          >
            📝 Notes
          </button>
          <button
            className={`${styles.toggleButton} ${contentType === 'video' ? styles.toggleButtonActive : ''}`}
            onClick={() => setContentType('video')}
            disabled={loading}
          >
            🎥 Videos
          </button>
        </div>
      </div>

      {alert && (
        <div className={`${styles.alert} ${
          alert.type === 'success' ? styles.alertSuccess : 
          alert.type === 'error' ? styles.alertError : 
          styles.alertInfo
        }`}>
          {alert.message}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
        </div>
      ) : mediaItems.length === 0 ? (
        <div className={styles.noData}>
          <h3>No media items found</h3>
          <p>
            {!sourceLanguage
              ? 'Please select a target language to view media items.'
              : `No ${contentType === 'video' ? 'videos' : 'notes'} found for ${selectedBaseLanguage} → ${sourceLanguage}.`}
          </p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Section #</th>
                <th style={{ width: '25%' }}>Section Name</th>
                <th style={{ width: '35%' }}>Description / Text</th>
                <th style={{ width: '20%' }}>Image / Video</th>
                <th style={{ width: '10%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupMediaBySection(mediaItems).map((group) => (
                <React.Fragment key={group.sectionId}>
                  {group.items.map((item, idx) => (
                    <tr key={`${item.languagePair}-${item.sectionKey}-${idx}`}>
                      {idx === 0 && (
                        <td rowSpan={group.items.length} className={styles.sectionNumberCell}>
                          <strong>{group.sectionNumber}</strong>
                        </td>
                      )}
                      {idx === 0 && (
                        <td rowSpan={group.items.length} className={styles.sectionNameCell}>
                          <strong>{group.sectionId}</strong>
                          <div className={styles.statusChips}>
                            <span className={`${styles.chip} ${item.status === 'published' ? styles.chipPublished : styles.chipDraft}`}>
                              {item.status}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className={styles.descriptionCell}>
                        {item.noteText ? (
                          <div className={styles.noteText}>{item.noteText}</div>
                        ) : item.noteImageDescription ? (
                          <div className={styles.noteDescription}>{item.noteImageDescription}</div>
                        ) : (
                          <em>No description</em>
                        )}
                      </td>
                      <td className={styles.mediaCell}>
                        {item.noteImageUrl || item.videoThumbnailUrl ? (
                          <div>
                            <img
                              src={item.noteImageUrl || item.videoThumbnailUrl}
                              alt={item.sectionId}
                              className={styles.thumbnail}
                            />
                            {item.videoUrl && (
                              <a
                                href={item.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.videoLink}
                                title="Open video in new tab"
                              >
                                🎥 Video
                              </a>
                            )}
                          </div>
                        ) : item.videoUrl ? (
                          <a
                            href={item.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.videoLink}
                            title="Open video in new tab"
                          >
                            🎥 Video Link
                          </a>
                        ) : (
                          <div className={styles.noImage}>No media</div>
                        )}
                      </td>
                      <td className={styles.actionsCell}>
                        <button
                          className={`${styles.actionButton} ${styles.editButton}`}
                          onClick={() => handleOpenDialog(item)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {editingItem ? 'Edit Section Media' : 'Add Section Media'}
              </h2>
              {!editingItem && (
                <div className={styles.modalInfo}>
                  <p className={styles.modalHint}>
                    <strong>Base Language:</strong> {selectedBaseLanguage === 'EN' ? 'English' :
                      selectedBaseLanguage === 'ES' ? 'Spanish' :
                      selectedBaseLanguage === 'FR' ? 'French' :
                      selectedBaseLanguage === 'DE' ? 'German' :
                      selectedBaseLanguage === 'IT' ? 'Italian' :
                      selectedBaseLanguage === 'PT' ? 'Portuguese' : selectedBaseLanguage}
                  </p>
                  <p className={styles.modalHint}>
                    💡 Tip: Use "Generate All Languages" to create media for all base languages at once, or use "Create" to save for {selectedBaseLanguage} only.
                  </p>
                </div>
              )}
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="sectionId">Section:</label>
                {editingItem ? (
                  <input
                    id="sectionId"
                    type="text"
                    value={formData.sectionId}
                    disabled={true}
                    className={styles.disabledInput}
                  />
                ) : (
                  <select
                    id="sectionId"
                    value={formData.sectionId}
                    onChange={(e) => handleInputChange('sectionId', e.target.value)}
                  >
                    <option value="">-- Select Section --</option>
                    {[...courseSections]
                      .sort((a, b) => {
                        const numA = parseInt(a.Identifier) || 999;
                        const numB = parseInt(b.Identifier) || 999;
                        return numA - numB;
                      })
                      .map((section) => (
                        <option key={section.Identifier} value={section.Vocabulary_List}>
                          {section.Identifier}. {section.Vocabulary_List}
                        </option>
                      ))}
                  </select>
                )}
              </div>

              {/* Show different fields based on content type */}
              {contentType === 'notes' ? (
                <>
                  {/* Notes Fields */}
                  <div className={styles.formGroup}>
                    <label htmlFor="imageUpload">Upload Section Image:</label>
                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedImageFile(file);
                          handleImageFileSelect(file);
                        }
                      }}
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <p>Uploading image...</p>}
                    {formData.noteImageUrl && (
                      <div>
                        <p>Current image:</p>
                        <img src={formData.noteImageUrl} alt="Section note" style={{maxWidth: '200px', maxHeight: '150px'}} />
                        {selectedImageFile && (
                          <button
                            type="button"
                            className={`${styles.button} ${styles.buttonSecondary}`}
                            onClick={handleOpenCropModal}
                            disabled={uploadingImage}
                            style={{marginTop: '10px'}}
                          >
                            Crop & Re-upload
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="noteImageDescription">Note Image Description:</label>
                    <input
                      id="noteImageDescription"
                      type="text"
                      value={formData.noteImageDescription}
                      onChange={(e) => handleInputChange('noteImageDescription', e.target.value)}
                      placeholder="Describe what's in the image"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="noteText">Note Text:</label>
                    <textarea
                      id="noteText"
                      value={formData.noteText}
                      onChange={(e) => handleInputChange('noteText', e.target.value)}
                      placeholder="Additional text notes for this section"
                    />
                  </div>

                  {/* Multi-language note generation - only for new items */}
                  {!editingItem && (
                    <div className={styles.formGroup}>
                      <div className={styles.multiLanguageSection}>
                        <h4 className={styles.sectionTitle}>Multi-Language Generation (Optional)</h4>
                        {!formData.noteImageUrl || !formData.noteText ? (
                          <p className={styles.requirementHint}>
                            ⚠️ Upload an image and enter note text above to enable multi-language generation
                          </p>
                        ) : null}
                        <div className={styles.formGroup}>
                          <label htmlFor="noteLanguage">Note Source Language:</label>
                          <select
                            id="noteLanguage"
                            value={narrationLanguage}
                            onChange={(e) => setNarrationLanguage(e.target.value)}
                            disabled={!formData.noteImageUrl || !formData.noteText}
                          >
                            <option value="EN">English</option>
                            <option value="ES">Spanish</option>
                            <option value="FR">French</option>
                            <option value="DE">German</option>
                            <option value="IT">Italian</option>
                            <option value="PT">Portuguese</option>
                          </select>
                          <small>Select the language of the note text you entered above</small>
                        </div>
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          onClick={handleGenerateMultiLanguageNotes}
                          disabled={generatingVideos || !formData.noteImageUrl || !formData.noteText}
                          style={{marginTop: '10px'}}
                        >
                          {generatingVideos ? 'Translating...' : 'Generate All Languages'}
                        </button>
                        {generationProgress && (
                          <p className={styles.progressText}>{generationProgress}</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Video Fields */}
                  <div className={styles.formGroup}>
                    <label htmlFor="imageUpload">Upload Video Background Image:</label>
                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedImageFile(file);
                          handleImageFileSelect(file);
                        }
                      }}
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <p>Uploading image...</p>}
                    {formData.noteImageUrl && (
                      <div>
                        <p>Current image:</p>
                        <img src={formData.noteImageUrl} alt="Video background" style={{maxWidth: '200px', maxHeight: '150px'}} />
                        {selectedImageFile && (
                          <button
                            type="button"
                            className={`${styles.button} ${styles.buttonSecondary}`}
                            onClick={handleOpenCropModal}
                            disabled={uploadingImage}
                            style={{marginTop: '10px'}}
                          >
                            Crop & Re-upload
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="videoUrl">Video URL (S3 Link):</label>
                    <input
                      id="videoUrl"
                      type="url"
                      value={formData.videoUrl}
                      onChange={(e) => handleInputChange('videoUrl', e.target.value)}
                      placeholder="https://your-bucket.s3.amazonaws.com/path/to/video.mp4"
                    />
                    <small>Enter the S3 URL for the video file. Do not upload video files directly.</small>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="videoScript">Video Script (for generation):</label>
                    <textarea
                      id="videoScript"
                      value={formData.videoScript}
                      onChange={(e) => handleInputChange('videoScript', e.target.value)}
                      placeholder="Enter the script text for multi-language video generation (optional if you already have a video URL)"
                      rows={4}
                    />
                    <small>Only needed if using "Generate All Languages" feature below</small>
                  </div>

                  {/* Multi-language video generation - only for new items */}
                  {!editingItem && (
                    <div className={styles.formGroup}>
                      <div className={styles.multiLanguageSection}>
                        <h4 className={styles.sectionTitle}>Multi-Language Generation (Optional)</h4>
                        {!formData.noteImageUrl || !formData.videoScript ? (
                          <p className={styles.requirementHint}>
                            ⚠️ Upload an image and enter video script above to enable multi-language generation
                          </p>
                        ) : null}
                        <div className={styles.formGroup}>
                          <label htmlFor="videoLanguage">Video Script Language:</label>
                          <select
                            id="videoLanguage"
                            value={narrationLanguage}
                            onChange={(e) => setNarrationLanguage(e.target.value)}
                            disabled={!formData.noteImageUrl || !formData.videoScript}
                          >
                            <option value="EN">English</option>
                            <option value="ES">Spanish</option>
                            <option value="FR">French</option>
                            <option value="DE">German</option>
                            <option value="IT">Italian</option>
                            <option value="PT">Portuguese</option>
                          </select>
                          <small>Select the language of the script text you entered above</small>
                        </div>
                        <div className={styles.buttonGroup}>
                          <button
                            type="button"
                            className={`${styles.button} ${styles.buttonSecondary}`}
                            onClick={handleGenerateTestAudio}
                            disabled={generatingVideos || !formData.noteImageUrl || !formData.videoScript}
                          >
                            {generatingVideos ? 'Generating...' : 'Test (English Only)'}
                          </button>
                          <button
                            type="button"
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            onClick={handleGenerateVideos}
                            disabled={generatingVideos || !formData.noteImageUrl || !formData.videoScript}
                          >
                            {generatingVideos ? 'Generating Audio...' : 'Generate All Languages'}
                          </button>
                        </div>
                        {generationProgress && (
                          <p className={styles.progressText}>{generationProgress}</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}


              <div className={styles.formGroup}>
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={handleCloseDialog}
              >
                Cancel
              </button>
              {!editingItem && (
                <div className={styles.actionHint}>
                  Save this media for <strong>{selectedBaseLanguage}</strong> only, or use "Generate All Languages" buttons above for bulk creation.
                </div>
              )}
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleSave}
                disabled={!formData.sectionId}
              >
                {editingItem
                  ? 'Save Changes'
                  : `Create for ${selectedBaseLanguage} Only`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Screen for Multi-Language Generation */}
      {generatingVideos && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <h3>Generating Multi-Language Content</h3>
            <p>{generationProgress}</p>
            <div className={styles.loadingSteps}>
              <div>• Translating text to all target languages</div>
              <div>• Processing content for each language</div>
              <div>• Preparing media entries</div>
              <div>• Ready for review and saving</div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Table - Before Saving to Database */}
      {previewMode && previewData.length > 0 && (
        <div className={styles.previewTable}>
          <div className={styles.previewHeader}>
            <h3>Preview Generated Media - Review Before Saving</h3>
            <div className={styles.previewActions}>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={handleDiscardPreview}
                disabled={savingToDB}
              >
                Discard
              </button>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleConfirmSave}
                disabled={savingToDB}
              >
                {savingToDB ? 'Saving...' : 'Confirm & Save to Database'}
              </button>
            </div>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Translated Text</th>
                  <th>Image</th>
                  <th>Audio</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((media, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{media.language}</strong>
                    </td>
                    <td>
                      <div className={styles.textCell}>
                        {media.translatedText}
                      </div>
                    </td>
                    <td>
                      <a 
                        href={media.imageUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.link}
                      >
                        View Image
                      </a>
                    </td>
                    <td>
                      {media.audioUrl ? (
                        <a
                          href={media.audioUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.link}
                        >
                          Play Audio
                        </a>
                      ) : (
                        <span style={{color: '#999'}}>N/A</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.statusPreview}>Preview - Not Saved</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {savingToDB && (
            <div className={styles.savingProgress}>
              <p>{generationProgress}</p>
            </div>
          )}
        </div>
      )}

      {/* Generated Media Table */}
      {generatedVideos.length > 0 && (
        <div className={styles.videosTable}>
          <h3>Generated Media (Audio + Image)</h3>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Translated Text</th>
                  <th>Image</th>
                  <th>Audio</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {generatedVideos.map((media, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{media.language}</strong>
                    </td>
                    <td>
                      <div className={styles.textCell}>
                        {media.translatedText}
                      </div>
                    </td>
                    <td>
                      <a 
                        href={media.imageUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.link}
                      >
                        View Image
                      </a>
                    </td>
                    <td>
                      {media.audioUrl ? (
                        <a
                          href={media.audioUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.link}
                        >
                          Play Audio
                        </a>
                      ) : (
                        <span style={{color: '#999'}}>N/A</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.statusSaved}>Saved to DB</span>
                    </td>
                    <td>
                      <button
                        className={`${styles.button} ${styles.buttonSmall}`}
                        onClick={() => {
                          // This edit button is for the preview/generated videos table
                          // Since we're previewing, we can't edit - this feature may need to be removed or updated
                          setAlert({ type: 'info', message: 'Please save the generated media first, then edit from the main table' });
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          fileName={fileNameToCrop}
        />
      )}
    </div>
  );
};

export default SectionMediaManager;